/**
 * WebDeptIntakeForm — multi-step public intake form for the Web Dept.
 *
 * Flow:
 *   Step 1 – Request  →  Step 2 – Identity  →  Step 3 – OTP verify  →  Panel redirect
 *
 * Environment variables required:
 *   NEXT_PUBLIC_PANEL_URL  — base URL of the panel backend (no trailing slash)
 *                            e.g. https://panel26.cloudaxe.io
 *
 * Panel API contracts consumed:
 *   GET  {PANEL_URL}/api/public/web-dept/templates        → { tiles: ServiceTile[] }
 *   POST {PANEL_URL}/api/v1/auth/otp/send                 → 200 | 4xx { success, message?, error? }
 *   POST {PANEL_URL}/api/v1/auth/otp/verify               → 200 | 4xx { success, message?, error? }
 *   OAuth (NextAuth, panel basePath /hosting-control-panel):
 *     {PANEL_URL}/hosting-control-panel/api/auth/signin/google?callbackUrl=...
 *     {PANEL_URL}/hosting-control-panel/api/auth/signin/apple?callbackUrl=...
 *     callbackUrl → .../auth/oauth-complete?returnTo=/dashboard/web-dept
 *   After successful OTP verify → redirect to
 *     {PANEL_URL}/hosting-control-panel/auth/otp-complete?returnTo=/dashboard/web-dept
 *     with draft details/siteDomain in the query and autoLoginToken in the hash
 */

"use client";

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { trackEvent } from "@/lib/analytics";

/* ─────────────────────────── Config ─────────────────────────────────────── */

const PANEL_URL = (process.env.NEXT_PUBLIC_PANEL_URL ?? "").replace(/\/$/, "");
const PANEL_BASE_PATH = "/hosting-control-panel";
const PANEL_OTP_COMPLETE_PATH = `${PANEL_BASE_PATH}/auth/otp-complete`;
const PANEL_OAUTH_COMPLETE_PATH = `${PANEL_BASE_PATH}/auth/oauth-complete`;
const OTP_DETAILS_MAX = 1000;
const OTP_SITE_DOMAIN_MAX = 191;
/** Temporarily hide Apple Sign In until provider setup is ready. */
const SHOW_APPLE_SIGN_IN = false;

/* ─────────────────────────── Types ─────────────────────────────────────── */

interface ServiceTile {
  id: string;
  label: string;
  icon: string;
  description?: string | null;
  keywords?: string | null;
  priceCents?: number | null;
  approvedAutomation?: boolean;
}

interface RequestData {
  tileId: string | null;
  freeText: string;
  domain: string;
}

type ContactMethod = "email" | "phone";
type Step = 1 | 2 | 3;

/* ─────────────────────────── Static data ───────────────────────────────── */

const DEFAULT_TILES: ServiceTile[] = [
  {
    id: "hacked-site",
    label: "Hacked Site",
    icon: "🛡️",
    keywords: "malware, hacked, virus, compromised, infected",
    description: "Remove malware and secure a compromised website.",
  },
  {
    id: "wordpress-critical-error",
    label: "WordPress Critical Error",
    icon: "⚠️",
    keywords: "wordpress, critical error, white screen, fatal error, 500 error",
    description: "Diagnose and fix WordPress critical errors.",
  },
  {
    id: "woocommerce",
    label: "WooCommerce",
    icon: "🛒",
    keywords: "woocommerce, store, ecommerce, checkout, cart",
    description: "Build or fix WooCommerce stores.",
  },
];

const DEFAULT_TILE_IDS = new Set(DEFAULT_TILES.map((t) => t.id));

/** Visual-only typewriter phrases for the Step 1 search pill. Index 0 plays once; 1–6 loop. */
const TYPEWRITER_PHRASES = [
  "How can we help?",
  "My site was hacked",
  "Fix my wordpress site",
  "Updating crashed my site",
  "Add CIPA compliance to my site",
  "My site is down",
  "Fix my site css styling in mobile",
];
/** Hold duration after a phrase finishes before the next phrase begins. */
const TYPEWRITER_CYCLE_MS = 6000;
/** Per-character typing speed for the search-bar typewriter. */
const TYPEWRITER_CHAR_MS = 34;

const COUNTRY_CODES = [
  { code: "+1", flag: "🇺🇸", label: "US/CA" },
  { code: "+44", flag: "🇬🇧", label: "UK" },
  { code: "+61", flag: "🇦🇺", label: "AU" },
  { code: "+33", flag: "🇫🇷", label: "FR" },
  { code: "+49", flag: "🇩🇪", label: "DE" },
  { code: "+34", flag: "🇪🇸", label: "ES" },
  { code: "+39", flag: "🇮🇹", label: "IT" },
  { code: "+81", flag: "🇯🇵", label: "JP" },
  { code: "+86", flag: "🇨🇳", label: "CN" },
  { code: "+91", flag: "🇮🇳", label: "IN" },
  { code: "+52", flag: "🇲🇽", label: "MX" },
  { code: "+55", flag: "🇧🇷", label: "BR" },
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
const E164_PATTERN = /^\+[1-9]\d{7,14}$/;

function normalizePhoneDigits(value: string): string {
  return value.replace(/\D/g, "");
}

function formatE164Phone(countryCode: string, nationalNumber: string): string {
  const countryDigits = normalizePhoneDigits(countryCode);
  const nationalDigits = normalizePhoneDigits(nationalNumber);
  if (!countryDigits || !nationalDigits) return "";
  return `+${countryDigits}${nationalDigits}`;
}

function validateContactInput(
  contactMethod: ContactMethod,
  email: string,
  phone: string,
  countryCode: string,
): string | null {
  if (contactMethod === "email") {
    const normalizedEmail = email.trim();
    if (!normalizedEmail) {
      return "Enter your email address.";
    }
    if (!EMAIL_PATTERN.test(normalizedEmail)) {
      return "Enter a valid email address (for example, you@example.com).";
    }
    return null;
  }

  const nationalNumber = phone.trim();
  if (!nationalNumber) {
    return "Enter your phone number.";
  }

  const e164Phone = formatE164Phone(countryCode, nationalNumber);
  if (!E164_PATTERN.test(e164Phone)) {
    return "Enter a valid phone number using your country code and mobile number.";
  }

  return null;
}

const SYSTEM_ISSUE_MESSAGE = "System issues. Please try again later.";

type PanelErrorPayload = {
  success?: boolean;
  message?: string;
  error?: string;
  autoLoginToken?: string;
};

function extractPanelErrorMessage(payload: PanelErrorPayload): string | undefined {
  const message = payload.message?.trim() || payload.error?.trim();
  return message || undefined;
}

function isSystemOtpFailure(status: number, message?: string): boolean {
  if (!PANEL_URL) return true;
  if (status === 0) return true;
  if (status >= 500) return true;
  if (status === 401 || status === 403 || status === 502 || status === 503 || status === 504) {
    return true;
  }

  if (!message) return false;

  const normalized = message.toLowerCase();
  return (
    normalized.includes("api key") ||
    normalized.includes("credential") ||
    normalized.includes("misconfigured") ||
    normalized.includes("voip") ||
    normalized.includes("sms provider") ||
    normalized.includes("internal server") ||
    normalized.includes("service unavailable") ||
    normalized.includes("temporarily unavailable")
  );
}

function resolveOtpSendError(status: number, message?: string): string {
  if (isSystemOtpFailure(status, message)) return SYSTEM_ISSUE_MESSAGE;
  if (message) return message;
  return "Failed to send code. Please try again.";
}

function resolveOtpVerifyError(status: number, message?: string): string {
  if (isSystemOtpFailure(status, message)) return SYSTEM_ISSUE_MESSAGE;
  if (message) return message;
  return "Invalid or expired code. Please try again.";
}

function isContactFieldErrorMessage(message: string | null): boolean {
  return Boolean(message) && message !== SYSTEM_ISSUE_MESSAGE;
}

/* ─────────────────────────── Tailwind shorthands ───────────────────────── */

const INPUT_CLS =
  "w-full rounded-xl bg-white/40 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-500 ring-1 ring-white/30 backdrop-blur-xl outline-none focus:ring-2 focus:ring-sky-400/40 transition dark:bg-white/10 dark:text-white dark:placeholder:text-white/40 dark:ring-white/15 dark:focus:ring-sky-400/30";

const INPUT_INVALID_CLS =
  "ring-red-400/50 focus:ring-red-400/40 dark:ring-red-400/40 dark:focus:ring-red-400/40";

const BTN_PRIMARY =
  "w-full rounded-xl bg-[#27598e] px-4 py-3 text-sm font-semibold text-white shadow-md hover:bg-[#1d4670] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed";

const BTN_GHOST =
  "rounded-xl px-4 py-3 text-sm font-medium text-slate-600 ring-1 ring-slate-200 hover:bg-slate-100 transition dark:text-white/70 dark:ring-white/20 dark:hover:bg-white/10";

type TooltipPlacement = "top" | "bottom";

function FormTooltip({
  text,
  placement = "top",
  className = "",
  children,
}: {
  text: string;
  placement?: TooltipPlacement;
  className?: string;
  children: ReactNode;
}) {
  const positionClasses =
    placement === "top"
      ? "bottom-full left-1/2 z-20 mb-2 w-max max-w-[16rem] -translate-x-1/2"
      : "top-full left-1/2 z-20 mt-2 w-max max-w-[16rem] -translate-x-1/2";

  const arrowClasses =
    placement === "top"
      ? "absolute left-1/2 top-full -translate-x-1/2 -translate-y-1/2"
      : "absolute left-1/2 bottom-full -translate-x-1/2 translate-y-1/2";

  return (
    <div className={`group/tooltip relative ${className}`}>
      <span
        className={`pointer-events-none absolute ${positionClasses} rounded-xl bg-slate-800/95 px-3 py-2 text-xs font-medium leading-relaxed tracking-wide text-white shadow-xl ring-1 ring-white/10 backdrop-blur-sm opacity-0 transition-opacity duration-200 delay-300 group-hover/tooltip:opacity-100 dark:bg-white/95 dark:text-slate-900 dark:ring-slate-200/20`}
        role="tooltip"
      >
        {text}
        <span
          className={`h-2.5 w-2.5 rotate-45 rounded-sm bg-slate-800/95 ring-1 ring-white/10 dark:bg-white/95 dark:ring-slate-200/20 ${arrowClasses}`}
          aria-hidden
        />
      </span>
      {children}
    </div>
  );
}

/* ─────────────────────────── Speech recognition types ─────────────────── */
/**
 * Minimal type contract for the Web Speech API. We avoid `any` and keep the
 * surface area small — only what the voice-input button uses.
 */

interface SpeechRecognitionAlternativeLike {
  transcript: string;
}

interface SpeechRecognitionResultLike {
  readonly length: number;
  [index: number]: SpeechRecognitionAlternativeLike;
}

interface SpeechRecognitionResultListLike {
  readonly length: number;
  [index: number]: SpeechRecognitionResultLike;
}

interface SpeechRecognitionEventLike {
  results: SpeechRecognitionResultListLike;
}

interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: (() => void) | null;
  onend: (() => void) | null;
}

type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;

declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  }
}

function getSpeechRecognitionCtor(): SpeechRecognitionConstructor | null {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition ?? window.webkitSpeechRecognition ?? null;
}

/**
 * Picks a browser-supported audio MIME type for MediaRecorder. Chrome records
 * `audio/webm;codecs=opus`, Firefox `audio/ogg;codecs=opus`, Safari `audio/mp4`.
 * Returns an empty string when the MediaRecorder API is unavailable.
 */
function getMediaRecorderMimeType(): string {
  if (typeof MediaRecorder === "undefined") return "";
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus",
    "audio/mp4",
    "audio/wav",
  ];
  for (const type of candidates) {
    try {
      if (MediaRecorder.isTypeSupported(type)) return type;
    } catch {
      // isTypeSupported may throw on unknown codecs — skip to the next candidate
    }
  }
  return "";
}

/**
 * Whether any voice-input path is available: native Web Speech API, or the
 * MediaRecorder + getUserMedia fallback. The mic is hidden when both are absent.
 */
function isVoiceInputSupported(): boolean {
  if (typeof window === "undefined") return false;
  if (getSpeechRecognitionCtor()) return true;
  if (typeof MediaRecorder === "undefined") return false;
  return (
    getMediaRecorderMimeType() !== "" &&
    typeof navigator !== "undefined" &&
    Boolean(navigator.mediaDevices?.getUserMedia)
  );
}

/* ─────────────────────────── Main component ────────────────────────────── */

export function WebDeptIntakeForm() {
  /* ── State ─────────────────────────────────────────────────────────── */
  const [step, setStep] = useState<Step>(1);
  const [tiles, setTiles] = useState<ServiceTile[]>(DEFAULT_TILES);

  const [request, setRequest] = useState<RequestData>({
    tileId: null,
    freeText: "",
    domain: "",
  });

  const [contactMethod, setContactMethod] = useState<ContactMethod>("email");
  const [email, setEmail] = useState("");
  const [countryCode, setCountryCode] = useState("+1");
  const [phone, setPhone] = useState("");

  const [otp, setOtp] = useState<string[]>(Array(6).fill(""));
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step1Error, setStep1Error] = useState<string | null>(null);
  const [isListening, setIsListening] = useState(false);
  const [showDomainField, setShowDomainField] = useState(false);
  const [voiceSupported, setVoiceSupported] = useState(true);
  const [typedText, setTypedText] = useState("");
  const [hasInteracted, setHasInteracted] = useState(false);

  const digitRefs = useRef<(HTMLInputElement | null)[]>([]);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const domainInputRef = useRef<HTMLInputElement | null>(null);
  /** Skip the autofocus focus event so the typewriter can run on mount. */
  const ignoreNextFocusRef = useRef(true);

  const typewriterVisible = step === 1 && !hasInteracted && request.freeText === "";

  /* ── Detect voice-input support (hide mic when neither path works) ──── */
  useEffect(() => {
    setVoiceSupported(isVoiceInputSupported());
  }, []);

  /* ── Search-bar typewriter (visual-only; never writes freeText) ─────── */
  useEffect(() => {
    if (!typewriterVisible) return;
    let cancelled = false;
    let timeout: ReturnType<typeof setTimeout>;

    const typePhrase = (phrase: string, onDone: () => void) => {
      setTypedText("");
      let i = 0;
      const tick = () => {
        if (cancelled) return;
        if (i <= phrase.length) {
          setTypedText(phrase.slice(0, i));
          i += 1;
          timeout = setTimeout(tick, i === 1 ? TYPEWRITER_CHAR_MS * 6 : TYPEWRITER_CHAR_MS);
        } else {
          onDone();
        }
      };
      tick();
    };

    let index = 0;
    const next = () => {
      if (cancelled) return;
      const phrase = TYPEWRITER_PHRASES[index];
      typePhrase(phrase, () => {
        timeout = setTimeout(() => {
          // After the intro (index 0), loop only the example requests (1–6).
          index = index === 0 ? 1 : (index % 6) + 1;
          next();
        }, TYPEWRITER_CYCLE_MS);
      });
    };
    next();

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [typewriterVisible]);

  /* ── Fetch tile set from panel (graceful fallback to defaults) ──────── */
  useEffect(() => {
    if (!PANEL_URL) return;
    const controller = new AbortController();
    fetch(`${PANEL_URL}/api/v1/web-dept/public/templates`, {
      signal: controller.signal,
      cache: "no-store",
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { tiles?: ServiceTile[] } | null) => {
        if (data?.tiles && Array.isArray(data.tiles) && data.tiles.length > 0) {
          setTiles(data.tiles.slice(0, 9));
        }
      })
      .catch(() => {
        // Network error or abort — silently keep defaults
      });
    return () => controller.abort();
  }, []);

  /* ── OTP countdown (resets whenever we enter step 3) ──────────────── */
  useEffect(() => {
    if (step !== 3) return;
    setCountdown(60);
    setCanResend(false);
    const timer = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(timer);
          setCanResend(true);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [step]);

  /* ── Auto-focus first OTP digit on step 3 ─────────────────────────── */
  useEffect(() => {
    if (step === 3) {
      setTimeout(() => digitRefs.current[0]?.focus(), 50);
    }
  }, [step]);

  /* ── Blur site hero once contact / OTP steps are active ───────────── */
  useEffect(() => {
    const root = document.documentElement;
    if (step >= 2) root.classList.add("intake-focus");
    else root.classList.remove("intake-focus");
    return () => root.classList.remove("intake-focus");
  }, [step]);

  /* ── Free-text tile auto-match ─────────────────────────────────────── */
  const handleFreeText = (text: string) => {
    setRequest((r) => ({ ...r, freeText: text }));
    if (!text.trim()) {
      setRequest((r) => ({ ...r, tileId: null }));
      return;
    }
    trackEvent("form_start", {
      form_type: "web_dept_intake",
      form_name: "web_support_ticket",
    });
    const lower = text.toLowerCase();
    // Autonomous identification: match against each template's comma-separated
    // keywords first, falling back to label words. A match is captured so the
    // matched template id (and its price) can be forwarded to the panel.
    const match = tiles.find((t) => {
      const terms = t.keywords
        ? t.keywords
            .split(",")
            .map((k) => k.trim().toLowerCase())
            .filter((k) => k.length > 2)
        : [];
      if (terms.length > 0) {
        return terms.some((term) => lower.includes(term));
      }
      return t.label
        .toLowerCase()
        .split(/\s+/)
        .some((word) => lower.includes(word) && word.length > 2);
    });
    setRequest((r) => ({ ...r, tileId: match ? match.id : null }));
  };

  /* ── Step 1 → 2 ────────────────────────────────────────────────────── */
  const hasRequestType = Boolean(request.tileId || request.freeText.trim());

  const handleStep1Continue = () => {
    if (!hasRequestType) {
      setStep1Error("Please tell us what you need help with.");
      return;
    }
    setStep1Error(null);

    // First continue click → reveal the optional Site/domain field and
    // move focus into it. Second click → advance to Step 2.
    if (!showDomainField) {
      setShowDomainField(true);
      setTimeout(() => domainInputRef.current?.focus(), 0);
      return;
    }
    setStep(2);
  };

  /* ── Voice input (Web Speech API, with MediaRecorder + Whisper fallback) ── */

  const stopMediaStream = () => {
    try {
      mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
    } catch {
      // Tracks may already be stopped — non-fatal
    }
    mediaStreamRef.current = null;
  };

  const stopListening = () => {
    try {
      recognitionRef.current?.stop();
    } catch {
      // Recognizer may already be stopped — non-fatal
    }
    try {
      if (mediaRecorderRef.current?.state === "recording") {
        mediaRecorderRef.current.stop();
      }
    } catch {
      // Recorder may already be stopped — non-fatal
    }
    setIsListening(false);
  };

  /** Records audio with MediaRecorder, then transcribes it via our Whisper route. */
  const startRecording = async () => {
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setStep1Error("Your microphone is unavailable. Please type your request.");
      return;
    }

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setStep1Error("Microphone access was denied. Please type your request.");
      return;
    }

    const mimeType = getMediaRecorderMimeType();
    if (!mimeType) {
      stopMediaStream();
      setStep1Error("Voice input is not supported in this browser. Please type your request.");
      return;
    }

    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream, { mimeType });
    } catch {
      stopMediaStream();
      setStep1Error("Voice input failed to start. Please type your request.");
      return;
    }

    const chunks: Blob[] = [];
    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) chunks.push(event.data);
    };
    recorder.onerror = () => {
      setIsListening(false);
      stopMediaStream();
      setStep1Error("Voice input failed. Please type your request.");
    };
    recorder.onstop = async () => {
      setIsListening(false);
      stopMediaStream();
      if (chunks.length === 0) return;

      const blobType = recorder.mimeType || mimeType || "audio/webm";
      const audioBlob = new Blob(chunks, { type: blobType });
      const extension = (blobType.split(";")[0] ?? "audio/webm").split("/")[1] ?? "webm";
      const file = new File([audioBlob], `voice.${extension}`, { type: blobType });

      const formData = new FormData();
      formData.append("audio", file);
      const lang = (navigator.language || "en").split("-")[0];
      if (lang) formData.append("language", lang);

      try {
        const res = await fetch("/api/speech-to-text", {
          method: "POST",
          body: formData,
        });
        const data = (await res.json().catch(() => ({}))) as { text?: string; error?: string };
        if (res.ok && data.text) {
          handleFreeText(data.text);
        } else {
          setStep1Error(data.error || "Could not transcribe audio. Please type your request.");
        }
      } catch {
        setStep1Error("Could not transcribe audio. Please type your request.");
      }
    };

    mediaStreamRef.current = stream;
    mediaRecorderRef.current = recorder;
    recorder.start();
    setIsListening(true);
    setStep1Error(null);
  };

  const startListening = () => {
    const Ctor = getSpeechRecognitionCtor();
    if (Ctor) {
      try {
        const recognition = new Ctor();
        recognition.lang =
          (typeof navigator !== "undefined" && navigator.language) || "en-US";
        recognition.interimResults = true;
        recognition.continuous = false;

        recognition.onresult = (event) => {
          let transcript = "";
          for (let i = 0; i < event.results.length; i++) {
            const result = event.results[i];
            if (result && result.length > 0) {
              transcript += result[0].transcript;
            }
          }
          if (transcript) {
            handleFreeText(transcript);
          }
        };
        recognition.onerror = () => {
          setIsListening(false);
        };
        recognition.onend = () => {
          setIsListening(false);
        };

        recognitionRef.current = recognition;
        recognition.start();
        setIsListening(true);
        setStep1Error(null);
        return;
      } catch {
        // Native recognition failed to start — fall through to the recorder path.
      }
    }

    void startRecording();
  };

  const toggleListening = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  /* ── Stop any active voice capture on unmount ──────────────────────── */
  useEffect(() => {
    return () => {
      try {
        recognitionRef.current?.abort();
      } catch {
        // Recognizer may already be gone — non-fatal
      }
      stopMediaStream();
    };
  }, []);

  /* ── Save pending request to localStorage (for panel auto-submit) ──── */
  const savePendingRequest = () => {
    try {
      localStorage.setItem(
        "webdept_pending_request",
        JSON.stringify({ ...request, ts: Date.now() }),
      );
    } catch {
      // localStorage may be unavailable in private browsing — non-fatal
    }
  };

  /* ── Send OTP via panel backend ────────────────────────────────────── */
  const sendOtp = useCallback(async () => {
    const validationError = validateContactInput(contactMethod, email, phone, countryCode);
    if (validationError) {
      setError(validationError);
      return;
    }

    if (!PANEL_URL) {
      setError(SYSTEM_ISSUE_MESSAGE);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const contact =
        contactMethod === "email"
          ? email.trim()
          : formatE164Phone(countryCode, phone);

      const body =
        contactMethod === "email"
          ? { email: contact, purpose: "ONBOARD" }
          : { phone: contact, purpose: "ONBOARD" };

      const res = await fetch(`${PANEL_URL}/api/v1/auth/otp/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await res.json().catch(() => ({})) as PanelErrorPayload;
      if (!res.ok || data.success === false) {
        setError(resolveOtpSendError(res.status, extractPanelErrorMessage(data)));
        return;
      }

      setStep(3);
    } catch (err: unknown) {
      if (err instanceof TypeError) {
        setError(SYSTEM_ISSUE_MESSAGE);
        return;
      }
      const message = err instanceof Error ? err.message : undefined;
      setError(resolveOtpSendError(0, message));
    } finally {
      setLoading(false);
    }
  }, [contactMethod, email, phone, countryCode]);

  /* ── Step 2 submit ─────────────────────────────────────────────────── */
  const handleStep2Submit = (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validateContactInput(contactMethod, email, phone, countryCode);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    sendOtp();
  };

  /* ── OAuth redirect (Google / Apple) ──────────────────────────────── */
  const handleOAuthRedirect = (provider: "google" | "apple") => {
    if (!PANEL_URL) {
      setError(SYSTEM_ISSUE_MESSAGE);
      return;
    }
    // Same-origin panel intake only; retail→panel OAuth cannot share this localStorage.
    savePendingRequest();
    trackEvent("generate_lead", {
      form_type: "web_dept_intake",
      form_name: "web_support_ticket",
      identity_method: provider,
    });
    const oauthCompleteUrl = new URL(`${PANEL_URL}${PANEL_OAUTH_COMPLETE_PATH}`);
    oauthCompleteUrl.searchParams.set("returnTo", "/dashboard/web-dept");
    const callbackUrl = encodeURIComponent(oauthCompleteUrl.toString());
    window.location.href = `${PANEL_URL}${PANEL_BASE_PATH}/api/auth/signin/${provider}?callbackUrl=${callbackUrl}`;
  };

  /* ── OTP digit input handlers ──────────────────────────────────────── */
  const handleDigitChange = (i: number, val: string) => {
    const digit = val.replace(/\D/g, "").slice(-1);
    const next = [...otp];
    next[i] = digit;
    setOtp(next);
    if (digit && i < 5) {
      digitRefs.current[i + 1]?.focus();
    }
  };

  const handleDigitKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !otp[i] && i > 0) {
      digitRefs.current[i - 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    const next = Array(6).fill("");
    for (let i = 0; i < 6; i++) {
      next[i] = pasted[i] ?? "";
    }
    setOtp(next);
    digitRefs.current[Math.min(pasted.length, 5)]?.focus();
  };

  /* ── Verify OTP ────────────────────────────────────────────────────── */
  const handleOtpVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = otp.join("");
    if (code.length < 6) return;
    if (!PANEL_URL) {
      setError(SYSTEM_ISSUE_MESSAGE);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const contact =
        contactMethod === "email"
          ? email.trim()
          : formatE164Phone(countryCode, phone);

      const res = await fetch(`${PANEL_URL}/api/v1/auth/otp/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contact, code }),
      });

      const data = await res.json().catch(() => ({})) as PanelErrorPayload;
      if (!res.ok || data.success === false) {
        setError(resolveOtpVerifyError(res.status, extractPanelErrorMessage(data)));
        setOtp(Array(6).fill(""));
        digitRefs.current[0]?.focus();
        return;
      }

      const autoLoginToken = data.autoLoginToken?.trim();
      if (!autoLoginToken) {
        setError(SYSTEM_ISSUE_MESSAGE);
        setOtp(Array(6).fill(""));
        digitRefs.current[0]?.focus();
        return;
      }

      const completeUrl = new URL(`${PANEL_URL}${PANEL_OTP_COMPLETE_PATH}`);
      completeUrl.searchParams.set("returnTo", "/dashboard/web-dept");
      const details = request.freeText.trim().slice(0, OTP_DETAILS_MAX);
      if (details) {
        completeUrl.searchParams.set("details", details);
      }
      const siteDomain = request.domain.trim().slice(0, OTP_SITE_DOMAIN_MAX);
      if (siteDomain) {
        completeUrl.searchParams.set("siteDomain", siteDomain);
      }
      // Forward the autonomously-matched service template id so the panel can
      // apply its automated pricing (approvedAutomation + priceCents) on create.
      const matchedTile = request.tileId ? tiles.find((t) => t.id === request.tileId) : undefined;
      if (matchedTile?.id) {
        completeUrl.searchParams.set("templateSetId", matchedTile.id);
      }
      completeUrl.hash = `token=${encodeURIComponent(autoLoginToken)}`;
      trackEvent("generate_lead", {
        form_type: "web_dept_intake",
        form_name: "web_support_ticket",
        identity_method: "otp",
      });
      window.location.href = completeUrl.toString();
    } catch (err: unknown) {
      if (err instanceof TypeError) {
        setError(SYSTEM_ISSUE_MESSAGE);
      } else {
        const message = err instanceof Error ? err.message : undefined;
        setError(resolveOtpVerifyError(0, message));
      }
      setOtp(Array(6).fill(""));
      digitRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  /* ─────────────────────── RENDER ───────────────────────────────────── */

  const contactFieldError = isContactFieldErrorMessage(error);

  return (
    <div className="p-0">
      {/* ── STEP 1: Request details ─────────────────────────────────── */}
      {step === 1 && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleStep1Continue();
          }}
          className="animate-fade-in-up"
          noValidate
        >
          {/* Pill-shaped chat input with embedded mic + continue buttons */}
          <FormTooltip
            text="Tell us what you need help with. Press the mic to speak, or the arrow to continue."
            className="block w-full"
            placement="bottom"
          >
            <div
              className={`flex items-center gap-2 rounded-full bg-white/50 py-2 pl-5 pr-2 ring-1 backdrop-blur-xl transition-shadow focus-within:ring-2 focus-within:ring-sky-400/40 dark:bg-white/10 ${
                step1Error
                  ? "ring-red-400/50 dark:ring-red-400/40"
                  : "ring-white/40 dark:ring-white/15"
              }`}
            >
              <div className="relative min-w-0 flex-1">
                <input
                  type="text"
                  className="w-full bg-transparent py-2 text-sm text-slate-900 placeholder:text-slate-500 outline-none dark:text-white dark:placeholder:text-white/50"
                  placeholder={typewriterVisible ? "" : "How can we help? ex: Remove Malware..."}
                  value={request.freeText}
                  onChange={(e) => {
                    setHasInteracted(true);
                    handleFreeText(e.target.value);
                    if (step1Error) setStep1Error(null);
                  }}
                  onFocus={() => {
                    // autoFocus fires once on mount — ignore it so the typewriter can run.
                    if (ignoreNextFocusRef.current) {
                      ignoreNextFocusRef.current = false;
                      return;
                    }
                    setHasInteracted(true);
                  }}
                  aria-label="How can we help?"
                  aria-invalid={Boolean(step1Error)}
                  autoFocus
                  autoComplete="off"
                />
                {typewriterVisible && (
                  <span
                    className="pointer-events-none absolute left-0 top-1/2 -translate-y-1/2 whitespace-nowrap text-sm text-slate-500 dark:text-white/50"
                    aria-hidden
                  >
                    {typedText}
                    <span className="ticker-caret-blink ml-0.5 inline-block">|</span>
                  </span>
                )}
              </div>

              {voiceSupported && (
                <FormTooltip
                  text={
                    isListening
                      ? "Listening… click to stop voice input."
                      : "Use your microphone to dictate your request."
                  }
                  placement="bottom"
                  className="shrink-0"
                >
                  <button
                    type="button"
                    onClick={toggleListening}
                    aria-label={isListening ? "Stop voice input" : "Start voice input"}
                    aria-pressed={isListening}
                    className={`flex h-9 w-9 items-center justify-center rounded-full transition-all active:scale-95 ${
                      isListening
                        ? "animate-pulse bg-red-500/90 text-white shadow ring-1 ring-red-300/60"
                        : "bg-white/60 text-slate-600 ring-1 ring-white/40 hover:bg-white/80 dark:bg-white/10 dark:text-white/80 dark:ring-white/15 dark:hover:bg-white/20"
                    }`}
                  >
                    {/* Microphone icon */}
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 1.75a3 3 0 00-3 3v7a3 3 0 006 0v-7a3 3 0 00-3-3z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 11a7 7 0 0014 0M12 18v3" />
                    </svg>
                  </button>
                </FormTooltip>
              )}

              <button
                type="submit"
                aria-label="Continue"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-900 text-white shadow transition-all hover:bg-slate-800 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-slate-900 dark:hover:bg-white/90"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 12h14M13 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </FormTooltip>

          {/* Suggestions ticker removed; spacer keeps former MotionTicker height (7em + mt-5). */}
          <div
            className="relative mt-5 max-w-[min(100%,42rem)] pl-5 pr-6"
            style={{ minHeight: "7em" }}
            aria-hidden
          />

          {/* Optional site/domain — revealed only after the first continue
              click, so the chat field stays the focal point initially. */}
          {showDomainField && (
            <FormTooltip
              text="Share the affected site, domain, or app name so we can investigate faster."
              className="mt-3 block w-full animate-fade-in"
            >
              <input
                ref={domainInputRef}
                type="text"
                className={INPUT_CLS}
                placeholder="Site / domain (optional)"
                value={request.domain}
                onChange={(e) => setRequest((r) => ({ ...r, domain: e.target.value }))}
                aria-label="Share the affected site, domain, or app name so we can investigate faster."
              />
            </FormTooltip>
          )}

          {step1Error && (
            <p className="mt-3 text-xs text-red-500 dark:text-red-400" role="alert">
              {step1Error}
            </p>
          )}
        </form>
      )}

      {/* ── STEP 2: Identity / onboarding ──────────────────────────── */}
      {step === 2 && (
        <div className="animate-fade-in-up">
          <p className="mb-4 inline-block rounded-lg bg-white/85 px-2.5 py-1.5 text-sm font-bold tracking-wide text-[#0f172b] shadow-[0_1px_0_rgba(255,255,255,0.9)] ring-1 ring-white/60 backdrop-blur-md dark:bg-slate-950/75 dark:text-white dark:ring-white/15">
            Enter your email or phone no#
          </p>

          {/* Email / Phone toggle */}
          <div className="mb-4 flex gap-1 rounded-xl bg-white/30 p-1 ring-1 ring-white/20 dark:bg-white/10 dark:ring-white/10">
            {(["email", "phone"] as const).map((method) => {
              const methodTooltip =
                method === "email"
                  ? "Receive a one-time code by email to sign in or create your account."
                  : "Receive a one-time code by text message to sign in or create your account.";

              return (
                <FormTooltip key={method} text={methodTooltip} className="min-w-0 flex-1">
                  <button
                    type="button"
                    onClick={() => { setContactMethod(method); setError(null); }}
                    aria-pressed={contactMethod === method}
                    aria-label={methodTooltip}
                    className={`w-full rounded-lg py-2 text-xs font-semibold transition-all capitalize ${
                      contactMethod === method
                        ? "bg-[#27598e] text-white shadow"
                        : "text-slate-500 hover:text-slate-800 dark:text-white/50 dark:hover:text-white"
                    }`}
                  >
                    {method === "email" ? "Email" : "Phone"}
                  </button>
                </FormTooltip>
              );
            })}
          </div>

          <form onSubmit={handleStep2Submit} noValidate className="space-y-3">
            {contactMethod === "email" ? (
              <FormTooltip
                text="We will send a one-time verification code to this email address."
                className="block w-full"
              >
                <input
                  type="email"
                  className={`${INPUT_CLS}${contactFieldError ? ` ${INPUT_INVALID_CLS}` : ""}`}
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError(null);
                  }}
                  required
                  autoFocus
                  autoComplete="email"
                  aria-invalid={contactFieldError}
                  aria-label="We will send a one-time verification code to this email address."
                />
              </FormTooltip>
            ) : (
              /* Phone number with country-code picker */
              <div className="flex gap-2">
                <FormTooltip
                  text="Choose the country code that matches the phone number you will verify."
                  className="w-[6.5rem] shrink-0"
                >
                  <select
                    className={`w-full rounded-xl bg-white/40 px-2 py-3 text-sm ring-1 ring-white/30 backdrop-blur-xl outline-none focus:ring-2 focus:ring-sky-400/40 transition dark:bg-white/10 dark:text-white dark:ring-white/15${contactFieldError ? ` ${INPUT_INVALID_CLS}` : ""}`}
                    value={countryCode}
                    onChange={(e) => {
                      setCountryCode(e.target.value);
                      setError(null);
                    }}
                    aria-invalid={contactFieldError}
                    aria-label="Choose the country code that matches the phone number you will verify."
                  >
                    {COUNTRY_CODES.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.flag} {c.code}
                      </option>
                    ))}
                  </select>
                </FormTooltip>
                <FormTooltip
                  text="Enter your mobile number without the country code; we will text a one-time verification code."
                  className="min-w-0 flex-1"
                >
                  <input
                    type="tel"
                    className={`w-full ${INPUT_CLS}${contactFieldError ? ` ${INPUT_INVALID_CLS}` : ""}`}
                    placeholder="Phone number"
                    value={phone}
                    onChange={(e) => {
                      setPhone(e.target.value);
                      setError(null);
                    }}
                    required
                    autoFocus
                    autoComplete="tel-national"
                    inputMode="tel"
                    aria-invalid={contactFieldError}
                    aria-label="Enter your mobile number without the country code; we will text a one-time verification code."
                  />
                </FormTooltip>
              </div>
            )}

            {error && (
              <p className="text-xs text-red-500 dark:text-red-400" role="alert">
                {error}
              </p>
            )}

            <FormTooltip
              text="Send a one-time verification code to the email or phone number you entered."
              className="block w-full"
              placement="bottom"
            >
              <button
                type="submit"
                disabled={loading}
                className={BTN_PRIMARY}
                aria-label="Send a one-time verification code to the email or phone number you entered."
              >
                {loading ? "Sending code…" : "Continue..."}
              </button>
            </FormTooltip>
          </form>

          {/* OAuth divider */}
          <div className="my-5 flex items-center gap-3">
            <div className="h-px flex-1 bg-slate-200 dark:bg-white/10" />
            <span className="text-xs text-slate-400 dark:text-white/40">or continue with</span>
            <div className="h-px flex-1 bg-slate-200 dark:bg-white/10" />
          </div>

          {/* OAuth buttons */}
          <div className={`grid gap-3 ${SHOW_APPLE_SIGN_IN ? "sm:grid-cols-2" : ""}`}>
            {/* Google */}
            <FormTooltip
              text="Continue with Google and open your Web Dept dashboard after sign-in."
              className="min-w-0"
            >
              <button
                type="button"
                onClick={() => handleOAuthRedirect("google")}
                aria-label="Continue with Google and open your Web Dept dashboard after sign-in."
                className="flex w-full items-center justify-center gap-2.5 rounded-xl bg-white/40 px-4 py-3 text-sm font-medium text-slate-700 ring-1 ring-slate-200/70 backdrop-blur-xl hover:bg-white/60 active:scale-[0.98] transition dark:bg-white/10 dark:text-white dark:ring-white/20 dark:hover:bg-white/15"
              >
              <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" aria-hidden>
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Sign in with Google
              </button>
            </FormTooltip>

            {/* Apple — hidden until SHOW_APPLE_SIGN_IN is true */}
            {SHOW_APPLE_SIGN_IN ? (
            <FormTooltip
              text="Continue with Apple ID and open your Web Dept dashboard after sign-in."
              className="min-w-0"
            >
              <button
                type="button"
                onClick={() => handleOAuthRedirect("apple")}
                aria-label="Continue with Apple ID and open your Web Dept dashboard after sign-in."
                className="flex w-full items-center justify-center gap-2.5 rounded-xl bg-white/40 px-4 py-3 text-sm font-medium text-slate-700 ring-1 ring-slate-200/70 backdrop-blur-xl hover:bg-white/60 active:scale-[0.98] transition dark:bg-white/10 dark:text-white dark:ring-white/20 dark:hover:bg-white/15"
              >
              <svg className="h-4 w-4 shrink-0 fill-current" viewBox="0 0 24 24" aria-hidden>
                <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
              </svg>
              Sign in with Apple
              </button>
            </FormTooltip>
            ) : null}
          </div>

          <FormTooltip
            text="Return to your request details without losing what you entered."
            className="mt-4 block w-full"
            placement="bottom"
          >
            <button
              type="button"
              className={`w-full ${BTN_GHOST}`}
              onClick={() => { setStep(1); setError(null); }}
              aria-label="Return to your request details without losing what you entered."
            >
              ← Back
            </button>
          </FormTooltip>
        </div>
      )}

      {/* ── STEP 3: OTP verification ────────────────────────────────── */}
      {step === 3 && (
        <div className="animate-fade-in-up">
          <p className="mb-1 inline-block rounded-lg bg-white/85 px-2.5 py-1.5 text-sm font-bold tracking-wide text-[#0f172b] shadow-[0_1px_0_rgba(255,255,255,0.9)] ring-1 ring-white/60 backdrop-blur-md dark:bg-slate-950/75 dark:text-white dark:ring-white/15">
            Enter your 6-digit code
          </p>
          <p className="mb-6 inline-block rounded-lg bg-white/80 px-2.5 py-1 text-xs font-medium text-[#0f172b] ring-1 ring-white/55 backdrop-blur-md dark:bg-slate-950/70 dark:text-white/80 dark:ring-white/15">
            We sent a code to{" "}
            <span className="font-semibold text-[#0f172b] dark:text-white">
              {contactMethod === "email" ? email : `${countryCode} ${phone}`}
            </span>
          </p>

          <form onSubmit={handleOtpVerify} className="space-y-4">
            {/* 6 individual digit boxes */}
            <FormTooltip
              text="Enter the 6-digit code from your email or text message. You can paste the full code."
              className="flex justify-center"
            >
              <div
                className="flex justify-center gap-2"
                onPaste={handleOtpPaste}
                role="group"
                aria-label="Enter the 6-digit code from your email or text message. You can paste the full code."
              >
                {otp.map((digit, i) => (
                  <FormTooltip
                    key={i}
                    text={`Enter digit ${i + 1} of your verification code.`}
                    placement="bottom"
                  >
                    <input
                      ref={(el) => { digitRefs.current[i] = el; }}
                      type="text"
                      inputMode="numeric"
                      pattern="\d"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleDigitChange(i, e.target.value)}
                      onKeyDown={(e) => handleDigitKeyDown(i, e)}
                      className="h-12 w-10 rounded-xl bg-white/75 text-center text-lg font-bold text-slate-900 ring-1 ring-white/50 backdrop-blur-xl outline-none focus:ring-2 focus:ring-sky-400/50 transition dark:bg-white/15 dark:text-white dark:ring-white/20 dark:focus:ring-sky-400/40"
                      aria-label={`Enter digit ${i + 1} of your verification code.`}
                      autoComplete={i === 0 ? "one-time-code" : "off"}
                    />
                  </FormTooltip>
                ))}
              </div>
            </FormTooltip>

            {error && (
              <p className="text-center text-xs text-red-500 dark:text-red-400" role="alert">
                {error}
              </p>
            )}

            <FormTooltip
              text="Confirm your code and open your Web Dept dashboard."
              className="block w-full"
              placement="bottom"
            >
              <button
                type="submit"
                disabled={loading || otp.join("").length < 6}
                className={BTN_PRIMARY}
                aria-label="Confirm your code and open your Web Dept dashboard."
              >
                {loading ? "Verifying…" : "Verify & continue →"}
              </button>
            </FormTooltip>
          </form>

          {/* Countdown + resend */}
          <p className="mt-4 text-center text-xs text-slate-500 dark:text-white/40">
            {canResend ? (
              <FormTooltip
                text="Send a fresh verification code to the same email or phone number."
                className="inline-flex"
                placement="bottom"
              >
                <button
                  type="button"
                  onClick={() => { setOtp(Array(6).fill("")); sendOtp(); }}
                  className="font-medium text-sky-600 hover:underline dark:text-sky-400"
                  aria-label="Send a fresh verification code to the same email or phone number."
                >
                  Resend code
                </button>
              </FormTooltip>
            ) : (
              <>Resend available in {countdown}s</>
            )}
          </p>

          <FormTooltip
            text="Return to change your email or phone number and request a new code."
            className="mt-4 block w-full"
            placement="bottom"
          >
            <button
              type="button"
              className={`w-full ${BTN_GHOST}`}
              onClick={() => { setStep(2); setOtp(Array(6).fill("")); setError(null); }}
              aria-label="Return to change your email or phone number and request a new code."
            >
              ← Back
            </button>
          </FormTooltip>
        </div>
      )}
    </div>
  );
}
